import { ICustomer } from 'shared-types/index';
import ObjectCopyService from 'external-services/object-copy-service/index';
import { AxiosInstance } from 'axios';
import AdminService from 'external-services/diary-admin-service/admin.service';
import NetworkConnectionService from 'external-services/network-connection-service/index';
import CustomerOfflineService from 'external-services/customer-offline-service/index';
import CustomerNormaliseService from 'external-services/customer-normalise-service/index';
import NetworkRequestsService from 'external-services/network-requests-service/index';
import PouchLoadService from 'external-services/pouch-load-service/index';
import CustomerLookupService from 'external-services/customer-lookup-service/index';
import { appURLS } from 'appRoot/app.constants';
import { IAuthService } from 'external-services/diary-admin-service/types';
import CompareObjectsService from "external-services/compare-objects-service/CompareObjects";

const NS = 'CustomerUpdateService'; // jshint ignore:line

const api: AxiosInstance = NetworkRequestsService.getGlobalAPI();

export const getApiInstanceForTests = (): AxiosInstance => api;

class CustomerUpdateService {
  private isAPIChannel: () => boolean;
  private trackEvent: (eventName: any, values: any) => void;
  private customerChangeUpdateAction: (payload?: ICustomer) => void;

  public setup(
    isAPIChannel: () => boolean,
    trackEvent: (eventName: any, values: any) => void,
    customerChangeUpdateAction: (payload?: ICustomer) => void,
  ): void {
    this.isAPIChannel = isAPIChannel;
    this.trackEvent = trackEvent;
    this.customerChangeUpdateAction = customerChangeUpdateAction;
  }


  // wrapper for addAsOnlineWhenPossible for those cases that don't care about isOnline value
  public add(customer: ICustomer): Promise<ICustomer> {
    return this.addAsOnlineWhenPossible(customer, true).then(({customer}) => customer);
  }

  /**
     * Add a new customer via API or into PouchDB
     * When store in PouchDB is a fallback caused by app offline status, mark the entry as temporary
     * @param {object} customer
     * @returns {promise}
     */
  public addAsOnlineWhenPossible(customer: ICustomer, addNewTempCustomer: boolean): Promise<{customer: ICustomer, isOnline: boolean}> {

    let customerDTO = ObjectCopyService.angularSafeCopy(customer);
    customerDTO = CustomerNormaliseService.normalizeViewToModel(customerDTO, this.isAPIChannel());

    const isOnline = this.isAPIChannel();
    if (isOnline) {
      return new Promise((resolve, reject) => {

        delete customerDTO.id;
        delete customerDTO.lastModifiedDate;

        api.post(AdminService.getVenueEndpointURL(), customerDTO).then((response: {data: ICustomer}) => {
          const customer = CustomerNormaliseService.normalizeModelToView(response.data, true);
          this.customerChangeUpdateAction();
          this.trackEvent('New Customer', {
            id: customer._id
          });
          resolve({customer, isOnline: true});
        }).catch(error => reject(error));
      });
    }

    if (!addNewTempCustomer) {
      return Promise.reject("offline");
    }

    customer._id = PouchLoadService.getNewId(['customer']);
    if (AdminService.getVenue().isOnlineCustomersEnabled) {
      customer.temporary = true;
    }
    return this.addInPouch(customer).then(customer => ({customer, isOnline: false}));
  }

  /**
     * Updates a customer with following logic:
     * 1) For an OnlineCustomer Venue
     *   A) App is online => use API
     *   B) App is offline => add or update into PouchDB accordingly
     * 2) For an PouchDB Venue update the customer in PouchDB
     *
     * @param {object} customer
     * @returns {promise}
     */
  public update(customer: ICustomer): Promise<ICustomer> {
    console.log('update---> customer', customer);
    this.trackEvent('Edit Customer', {
      id: customer._id,
      rev: customer._rev
    });

    let customerDTO = ObjectCopyService.angularSafeCopy(customer);
    customerDTO = CustomerNormaliseService.normalizeViewToModel(customerDTO, this.isAPIChannel());

    // OfflineCustomer Venue
    if (!AdminService.getVenue().isOnlineCustomersEnabled) {
        return this.updateInPouch(customerDTO);
    }

    // OnlineCustomer Venue / Online App
    if (!this.isOffline()) {
      return new Promise((resolve, reject) => {

        const customerId = customerDTO.id;
        delete customerDTO.id;
        delete customerDTO.lastModifiedDate;

        api.put(AdminService.getVenueEndpointURL() + '/' + customerId, customerDTO).then((response: {data: ICustomer}) => {
          const customer: ICustomer = CustomerNormaliseService.normalizeModelToView(response.data, true);
          // Notify customers list changed and a customer has been updated
          this.customerChangeUpdateAction();
          this.customerChangeUpdateAction(customer);
          resolve(customer);
        }).catch(error => reject(error));
      });
    }

    // OnlineCustomer Venue / Offline App
    return CustomerLookupService.getById(customerDTO._id || customerDTO.id).then((customer: ICustomer) => {
      if (customer != null) {
          return this.updateInPouch(customer);
      } else {
          return this.addInPouch(customer);
      }
    });
  }

  /**
     * Delete a customer's bookings, on success delete the customer itself
     * @param  {object} customer
     * @return {promise}
     */
  public remove(customer: ICustomer): Promise<boolean> {

    return new Promise((resolve, reject) => {

      if (this.isOffline()) {
        // TODO Sergio - should allow to delete offline pouchdDb temporary customers ??
        reject('Can not delete customer while app is offline');
        return;
      }

      this.trackEvent('Delete Customer', {
        id: customer._id,
        rev: customer._rev
      });

      let deleteBookingsPromise: Promise<any>, deleteCustomerPromise: Promise<any>;

      if (AdminService.getVenue().isOnlineCustomersEnabled) {
        deleteBookingsPromise = Promise.resolve([]);
        deleteCustomerPromise = api.delete(AdminService.getVenueEndpointURL() + '/' + customer._id);
      } else {
        deleteBookingsPromise = api.delete(appURLS.API + '/restaurants/venues/' + AdminService.getVenue().id + '/bookings', {
          params: {
            customerId: customer._id
          }
        });
        deleteCustomerPromise = CustomerOfflineService.removeOffline(customer);
      }

      // Successfully delete linked bookings before remove the customer
      deleteBookingsPromise.then(() => {
        // Delete customer
        deleteCustomerPromise.then(() => {
          this.customerChangeUpdateAction();
          resolve(true);
        }).catch((err: any) => {
          console.error('CustomerService.remove failed delete customer', err);
          reject(err);
        });
      }).catch((err: any) => {
        console.error('CustomerService.remove linked bookings failed', err);
        reject(err);
      }); // delete bookings

    }); // $q

  }

  /**
   * Checks if customer updates should be saved when booking is saved
   */
  public shouldForceUpdateCustomer(venueId: number, currentCustomer: ICustomer, originalCustomer: ICustomer): boolean {
    if (!currentCustomer) {
      return false;
    }
    if (currentCustomer.venueIds && venueId && !currentCustomer.venueIds.includes(venueId)) {
      return true;
    }

    if (!originalCustomer) {
      return true;
    }

    const customersAreIdentical = CompareObjectsService.hasIdenticalData(currentCustomer, originalCustomer);
    if (customersAreIdentical) {
      return false;
    }
    return true;
  }

  private addInPouch(customer: ICustomer): Promise<ICustomer> {
    return new Promise((resolve, reject) => {
      this.trackEvent('New Customer', {
        id: customer._id
      });

      customer = CustomerNormaliseService.normalizeViewToModel(customer);
      return PouchLoadService.getDbLocal().put(customer).then((info: { id: string; rev: string; }) => {
        customer._id = info.id;
        customer._rev = info.rev;
        customer = CustomerNormaliseService.normalizeModelToView(customer);
        if (CustomerOfflineService.getCustomerCache()) {
          CustomerOfflineService.appendToCustomerCache(customer);
        }
        this.customerChangeUpdateAction();
        resolve(customer);
      }).catch((error: any) => reject(error));
    });
  }

  private updateInPouch(customer: ICustomer): Promise<ICustomer> {
    return new Promise((resolve, reject) => {

      customer = CustomerNormaliseService.normalizeViewToModel(customer);

      PouchLoadService.getDbLocal().put(customer).then((info: { rev: string; }) => {
        customer._rev = info.rev;
        customer = CustomerNormaliseService.normalizeModelToView(customer);
        // Notify customers list changed and a customer has been updated
        this.customerChangeUpdateAction();
        this.customerChangeUpdateAction(customer);
        // Update internal cache
        if (CustomerOfflineService.getCustomerCache()) {
          const idx = CustomerOfflineService.getCustomerCache().findIndex((c: {_id: string;}) => c._id === customer._id);
          idx > -1 && CustomerOfflineService.updateCacheByIndex(idx, customer);
        }
        // Complete operation
        resolve(customer);
      }).catch((e: any) => {
          reject(e);
      });
    });
  }

  private isOffline(): boolean {
    return !NetworkConnectionService.isOnline();
  }
}

const instance = new CustomerUpdateService();
export default instance as CustomerUpdateService;
